// Dependencies
import React, { FunctionComponent, useState, useRef, useEffect, Fragment, useCallback } from "react";
import marked from "marked";

// Components
import { ConditionallyRender } from "component-library/utilities/conditionally-render";
import { Overlay } from "components/Overlay";
import { Asset } from "component-library/utilities/asset";
import Spinner from "components/Spinner";

// Utils
import { theme } from "config/theme";

// Styles
import {
    VirtualAssistantChatContainer,
    VirtualAssistantChatHeader,
    VirtualAssistantChatLabelsWrapper,
    VirtualAssistantChatCloseIcon,
    VirtualAssistantChatAssistantName,
    VirtualAssistantChatVersionLabelWrapper,
    VirtualAssistantChatVersionLabel,
    VirtualAssistantChatMessageCompositionWrapper,
    VirtualAssistantChatTextAreaWrapper,
    VirtualAssistantChatClosedChatIconWrapper,
    VirtualAssistantChatMessageContainer,
    VirtualAssistantChatMessageAvatarWrapper,
    VirtualAssistantChatMessageContentWrapper,
    VirtualAssistantChatMessageContent,
    VirtualAssistantChatActionCompositionWrapper,
    VirtualAssistantChatMessageIconContainer,
    VirtualAssistantChatTextArea,
    VirtualAssistantChatSendMessageButton,
    VirtualAssistantChatMobileWrapper,
    VirtualAssistantChatSuggestionMessageLabel,
    VirtualAssistantChatSuggestionMessageContainer,
    VirtualAssistantChatSuggestionMessageWrapper,
    VirtualAssistantChatSuggestionMessageTitle,
    VirtualAssistantChatTextAreaOverlay,
    VirtualAssistantChatMessageSpinnerWrapper,
    VirtualAssistantChatLoader
} from "./VirtualAssistantChat.styles";

// Types
import {
    VirtualAssistantChatProps,
    VirtualAssistantChatMessageProps,
    VirtualAssistantChatMessageIconProps,
    VirtualAssistantChatSuggestionMessageProps
} from "./VirtualAssistantChat.types";

export const VirtualAssistantChat: FunctionComponent<VirtualAssistantChatProps> = ({
    shouldDisplayEmptyState,
    shouldDisplaySuggestions,
    headerIconSource,
    closeIconSource,
    closedChatIconSource,
    sendMessageIconSource,
    assistantName,
    sendMessagePlaceholder,
    versionLabel,
    messages,
    isSendingMessage,
    isFetching,
    isLoading,
    emptyStateComposition,
    handleSendMessage,
    suggestionsComposition,
    fetchAnswers
}) => {
    const chatRef = useRef<HTMLDivElement>(null);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const suggestionsRef = useRef<HTMLDivElement>(null);

    const [isExpanded, setIsExpanded] = useState(false);
    const [hasAlreadyFetchedData, setHasAlreadyFetchedData] = useState(false);

    const textAreaDefaultHeightValue = "22px";

    useEffect(() => {
        if (!!suggestionsRef.current && !!shouldDisplaySuggestions) {
            suggestionsRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [shouldDisplaySuggestions]);

    const handleCloseChat = useCallback(() => {
        window.dispatchEvent(new CustomEvent("virtual-assistant-has-closed-chat"));
        setIsExpanded(false);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const toastElement = (event.target as HTMLElement).closest(".Toastify__toast");

            if (chatRef.current && (chatRef.current.contains(event.target as Node) || toastElement)) {
                return;
            }

            handleCloseChat();
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                handleCloseChat();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [handleCloseChat]);

    const handleVirtualAssistantSendMessage = useCallback(
        (question?: string) => {
            handleSendMessage(question);

            if (textAreaRef.current) {
                textAreaRef.current.value = "";
                textAreaRef.current.style.height = textAreaDefaultHeightValue;
                textAreaRef.current.blur();
            }
        },
        [handleSendMessage]
    );

    const textAreaAutoResize = () => {
        if (textAreaRef.current) {
            textAreaRef.current.style.height = textAreaDefaultHeightValue;
            textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
        }
    };

    useEffect(() => {
        if (!!isExpanded) {
            textAreaAutoResize();
        }
    }, [isExpanded]);

    useEffect(() => {
        if (!!isExpanded && !hasAlreadyFetchedData) {
            fetchAnswers();
            setHasAlreadyFetchedData(true);
        }
    }, [fetchAnswers, hasAlreadyFetchedData, isExpanded]);

    useEffect(() => {
        if (!!isExpanded) {
            const handleEnterPress = (event: KeyboardEvent) => {
                if (event.key === "Enter" && textAreaRef.current?.value) {
                    handleVirtualAssistantSendMessage(textAreaRef.current.value);
                }
            };

            document.addEventListener("keydown", handleEnterPress);

            return () => {
                document.removeEventListener("keydown", handleEnterPress);
            };
        }
    }, [handleVirtualAssistantSendMessage, isExpanded]);

    const expandedComposition = (
        <VirtualAssistantChatContainer ref={chatRef}>
            <VirtualAssistantChatHeader>
                <VirtualAssistantChatLabelsWrapper>
                    <Asset source={headerIconSource} color={theme.colors.brand[300]} size={24} />

                    <VirtualAssistantChatAssistantName>{assistantName}</VirtualAssistantChatAssistantName>

                    <VirtualAssistantChatVersionLabelWrapper>
                        <VirtualAssistantChatVersionLabel>{versionLabel}</VirtualAssistantChatVersionLabel>
                    </VirtualAssistantChatVersionLabelWrapper>
                </VirtualAssistantChatLabelsWrapper>

                <VirtualAssistantChatCloseIcon src={closeIconSource} onClick={handleCloseChat} alt="Fechar assistente virtual" />
            </VirtualAssistantChatHeader>

            <ConditionallyRender
                shouldRender={!!isFetching}
                content={
                    <VirtualAssistantChatMessageSpinnerWrapper>
                        <Spinner />
                    </VirtualAssistantChatMessageSpinnerWrapper>
                }
            />

            <ConditionallyRender
                shouldRender={!isFetching}
                content={
                    <VirtualAssistantChatMessageCompositionWrapper hasMessage={!!messages?.length}>
                        <ConditionallyRender shouldRender={!!shouldDisplayEmptyState} content={emptyStateComposition} />

                        <ConditionallyRender
                            shouldRender={!shouldDisplayEmptyState}
                            content={(messages ?? []).map((message, index) => (
                                <VirtualAssistantChatMessage key={`virtual-assistant-chat-message-${index}`} {...message} />
                            ))}
                        />

                        <ConditionallyRender
                            shouldRender={!!shouldDisplaySuggestions}
                            content={<div ref={suggestionsRef}>{suggestionsComposition}</div>}
                        />
                    </VirtualAssistantChatMessageCompositionWrapper>
                }
            />

            <VirtualAssistantChatTextAreaWrapper>
                <ConditionallyRender shouldRender={!!isSendingMessage || !!isFetching} content={<VirtualAssistantChatTextAreaOverlay />} />

                <VirtualAssistantChatTextArea ref={textAreaRef} placeholder={sendMessagePlaceholder} onChange={textAreaAutoResize} />

                <VirtualAssistantChatSendMessageButton type="button" onClick={() => handleVirtualAssistantSendMessage(textAreaRef.current?.value)}>
                    <img src={sendMessageIconSource} alt="Enviar pergunta" />
                </VirtualAssistantChatSendMessageButton>
            </VirtualAssistantChatTextAreaWrapper>
        </VirtualAssistantChatContainer>
    );

    return (
        <Fragment>
            <ConditionallyRender
                shouldRender={!isExpanded}
                content={
                    <VirtualAssistantChatClosedChatIconWrapper isLoading={isLoading} onClick={() => !isLoading && setIsExpanded(true)}>
                        <ConditionallyRender
                            shouldRender={!!isLoading}
                            content={
                                <VirtualAssistantChatLoader>
                                    <div>
                                        <div />
                                        <div />
                                        <div />
                                        <div />
                                    </div>
                                </VirtualAssistantChatLoader>
                            }
                        />

                        <ConditionallyRender
                            shouldRender={!isLoading}
                            content={<Asset source={closedChatIconSource} color={theme.colors.brand[300]} size={24} />}
                        />
                    </VirtualAssistantChatClosedChatIconWrapper>
                }
            />
            <ConditionallyRender
                shouldRender={!!isExpanded}
                maximumBreakpoint="tablet"
                content={
                    <Overlay
                        handleOutsideClick={handleCloseChat}
                        content={<VirtualAssistantChatMobileWrapper>{expandedComposition}</VirtualAssistantChatMobileWrapper>}
                    />
                }
            />
            <ConditionallyRender shouldRender={!!isExpanded} minimumBreakpoint="tablet" content={expandedComposition} />
        </Fragment>
    );
};

export const VirtualAssistantChatMessage: FunctionComponent<VirtualAssistantChatMessageProps> = ({
    id,
    content,
    variant,
    avatar,
    hasTypingAnimation,
    actionComposition,
    handleMessageCompletelyDisplayed
}) => {
    const messageEndRef = useRef<HTMLDivElement>(null);

    const [displayedText, setDisplayedText] = useState("");
    const [shouldAbortTyping, setShouldAbortTyping] = useState(false);

    useEffect(() => {
        const handleAbortTyping = () => {
            setShouldAbortTyping(true);
        };

        window.addEventListener("virtual-assistant-should-abort-typing-animation", handleAbortTyping);

        return () => {
            window.removeEventListener("virtual-assistant-should-abort-typing-animation", handleAbortTyping);
        };
    }, []);

    const handleTypingEnded = useCallback(() => {
        window.dispatchEvent(new CustomEvent("virtual-assistant-message-typing-end", { detail: { id } }));
    }, [id]);

    useEffect(() => {
        const handleTypingEnd = () => {
            handleTypingEnded();
        };

        window.addEventListener("virtual-assistant-has-closed-chat", handleTypingEnd);

        return () => {
            window.removeEventListener("virtual-assistant-has-closed-chat", handleTypingEnd);
        };
    }, [handleTypingEnded]);

    useEffect(() => {
        setDisplayedText("");

        if (variant === "default" && !!hasTypingAnimation) {
            let currentIndex = 0;
            let messageAux = "";

            const typingInterval = setInterval(() => {
                if (!!shouldAbortTyping) {
                    setDisplayedText(content);
                    handleTypingEnded();
                    clearInterval(typingInterval);
                }

                if (currentIndex < content.length) {
                    if (["<", "[", "{", "("].includes(content[currentIndex])) {
                        currentIndex++;
                        messageAux += content[currentIndex];
                        return;
                    }

                    messageAux += content[currentIndex];
                    setDisplayedText((prevText) => prevText + messageAux);
                    messageAux = "";

                    currentIndex++;
                    return;
                }

                if (!!handleMessageCompletelyDisplayed) {
                    handleMessageCompletelyDisplayed();
                }

                handleTypingEnded();
                setShouldAbortTyping(false);
                clearInterval(typingInterval);
            }, 25);

            return () => clearInterval(typingInterval);
        } else {
            setDisplayedText(content);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content, variant, hasTypingAnimation, shouldAbortTyping]);

    useEffect(() => {
        if (messageEndRef.current) {
            messageEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [displayedText]);

    return (
        <VirtualAssistantChatMessageContainer variant={variant}>
            <ConditionallyRender
                shouldRender={!!avatar}
                content={
                    <VirtualAssistantChatMessageAvatarWrapper>
                        <Asset source={avatar as string} color={theme.colors.brand[300]} size={16} />
                    </VirtualAssistantChatMessageAvatarWrapper>
                }
            />

            <VirtualAssistantChatMessageContentWrapper hasSpacing={!!actionComposition}>
                <VirtualAssistantChatMessageContent
                    variant={variant}
                    dangerouslySetInnerHTML={{
                        __html: marked.parse(displayedText.replace(/(?:\r\n|\r|\n)/g, "<br />"))
                    }}
                />

                <ConditionallyRender
                    shouldRender={!!actionComposition}
                    content={<VirtualAssistantChatActionCompositionWrapper>{actionComposition}</VirtualAssistantChatActionCompositionWrapper>}
                />

                <div ref={messageEndRef} />
            </VirtualAssistantChatMessageContentWrapper>
        </VirtualAssistantChatMessageContainer>
    );
};

export const VirtualAssistantChatMessageIcon: FunctionComponent<VirtualAssistantChatMessageIconProps> = ({ iconSource, isActive, handleClick }) => {
    return (
        <VirtualAssistantChatMessageIconContainer onClick={handleClick} isActive={isActive}>
            <img src={iconSource} alt="" />
        </VirtualAssistantChatMessageIconContainer>
    );
};

export const VirtualAssistantChatSuggestionMessage: FunctionComponent<VirtualAssistantChatSuggestionMessageProps> = ({
    label,
    messages,
    handleClick
}) => {
    return (
        <VirtualAssistantChatSuggestionMessageContainer>
            <VirtualAssistantChatSuggestionMessageLabel>{label}</VirtualAssistantChatSuggestionMessageLabel>

            {(messages ?? []).map((item, index) => (
                <VirtualAssistantChatSuggestionMessageWrapper key={`virtual-assistant-suggestion-message-${index}`} onClick={() => handleClick(item)}>
                    <VirtualAssistantChatSuggestionMessageTitle>{item}</VirtualAssistantChatSuggestionMessageTitle>
                </VirtualAssistantChatSuggestionMessageWrapper>
            ))}
        </VirtualAssistantChatSuggestionMessageContainer>
    );
};
